import { LOCAL_STORAGE_SCHEMA_VERSION } from './autosave-config';
import type { AudienceDraft, DraftPatch } from '../types/draft';

interface LocalMeta {
  lastEditedAt: string;
  schemaVersion: number;
  draft: DraftPatch;
}

export type ReconcileAction =
  | { action: 'use-server' }
  | { action: 'use-local' }
  | { action: 'prompt-user' };

/**
 * Reconcile a locally-mirrored draft with the server's authoritative state.
 *
 * We prompt the user ONLY when two conditions hold:
 *   1. Local content actually differs from server content (deep equality check).
 *   2. Local edit timestamp is strictly newer than the server's updatedAt.
 *
 * This eliminates false positives from phantom writes (hydration side effects,
 * query-driven programmatic mutations, etc.). A phantom write produces content
 * equal to the server, so the deep-equal check suppresses the prompt.
 */
export function reconcileDraft(input: {
  local: LocalMeta | null;
  server: AudienceDraft;
}): ReconcileAction {
  const { local, server } = input;
  if (!local) return { action: 'use-server' };
  if (local.schemaVersion !== LOCAL_STORAGE_SCHEMA_VERSION) return { action: 'use-server' };
  if (draftContentEquals(local.draft, server)) return { action: 'use-server' };
  if (new Date(local.lastEditedAt).getTime() > new Date(server.updatedAt).getTime()) {
    return { action: 'prompt-user' };
  }
  return { action: 'use-server' };
}

/**
 * Deep-equal draft content across the fields that end up in the payload.
 *
 * Written as one `&&` chain rather than a ladder of `if (!eq) return false`:
 * short-circuiting makes the two forms behaviourally identical (each term is
 * still only evaluated if every earlier one held), but a run of the same logical
 * operator costs a single point of cognitive complexity where each `if` cost
 * one — which is what keeps this function under Sonar's limit as fields are
 * added to the draft.
 */
export function draftContentEquals(a: DraftPatch, b: AudienceDraft | DraftPatch): boolean {
  const at = a.target ?? {};
  const bt = b.target ?? {};
  const ac = a.content ?? {};
  const bc = b.content ?? {};
  const ap = a.products ?? {};
  const bp = b.products ?? {};
  // requestedProducts and selectedProducts are categorical SETS (no semantic
  // ordering), so both are compared sorted to avoid spurious diffs when the
  // server returns them in a different order than the client persisted.
  //
  // Both comparators are explicit (SonarQube S2871). For selectedProducts that
  // is a fix, not just lint: the bare `.sort()` this replaces was a no-op —
  // the default comparator stringifies each element, so every array of objects
  // became a list of identical "[object Object]" keys, every pair compared
  // equal, and a reorder still read as a change. Products are keyed by slug
  // (`id`), which is what makes the set comparison stable.
  const byString = (x: string, y: string): number => x.localeCompare(y);
  const byProductId = (x: { id: string }, y: { id: string }): number => x.id.localeCompare(y.id);

  return (
    (at.name ?? null) === (bt.name ?? null) &&
    stringEquals(at.brief ?? '', bt.brief ?? '') &&
    numberOrNullEquals(at.leadGoal ?? null, bt.leadGoal ?? null) &&
    jsonEquals(at.leadsPerCompany ?? null, bt.leadsPerCompany ?? null) &&
    jsonEquals(at.campaignTimeframe ?? null, bt.campaignTimeframe ?? null) &&
    // NPJB-552 — compared independently; a change to either side must dirty the patch.
    stringEquals(at.startDate ?? '', bt.startDate ?? '') &&
    stringEquals(at.endDate ?? '', bt.endDate ?? '') &&
    numberOrNullEquals(at.impressionGoal ?? null, bt.impressionGoal ?? null) &&
    stringEquals(at.additionalDetails ?? '', bt.additionalDetails ?? '') &&
    jsonEquals(activeFilters(at.filters), activeFilters(bt.filters)) &&
    filesEqual(at.files ?? [], bt.files ?? []) &&
    jsonEquals(at.intentTopicSelections ?? [], bt.intentTopicSelections ?? []) &&
    // Without this a technographics-only edit compares EQUAL to the saved draft, so the
    // reconciler reports "no change" and the autosave never fires.
    jsonEquals(at.technographicSelections ?? [], bt.technographicSelections ?? []) &&
    jsonEquals(at.signalSources ?? [], bt.signalSources ?? []) &&
    // NPJB-763 — a hand edit to the domains, or a Refresh, must dirty the patch.
    (at.play ?? null) === (bt.play ?? null) &&
    jsonEquals(at.companyDomains ?? null, bt.companyDomains ?? null) &&
    contentLinksEqual(ac.links ?? [], bc.links ?? []) &&
    contentFilesEqual(ac.files ?? [], bc.files ?? []) &&
    jsonEquals(
      [...(ac.requestedProducts ?? [])].sort(byString),
      [...(bc.requestedProducts ?? [])].sort(byString),
    ) &&
    jsonEquals(
      [...(ap.selectedProducts ?? [])].sort(byProductId),
      [...(bp.selectedProducts ?? [])].sort(byProductId),
    ) &&
    numberOrNullEquals(ap.budget ?? null, bp.budget ?? null)
  );
}

function stringEquals(a: string, b: string): boolean {
  return a === b;
}

/**
 * Filter selections with the empty keys dropped and the rest key-sorted.
 *
 * A filter carrying no values is indistinguishable from one that isn't there: the
 * store always holds every key in FILTER_CONFIGS (via `emptyFilters()`), while a
 * saved draft only holds the keys that had values when it was written. Comparing
 * raw therefore reports a change for every filter added since — NPJB-553's
 * `jobTitle` would have marked every pre-existing draft dirty on load and could
 * have raised a phantom recovery prompt. Sorting the keys additionally makes the
 * comparison immune to insertion order, which `jsonEquals` would otherwise see.
 */
function activeFilters(filters: Record<string, unknown[]> | undefined): Record<string, unknown[]> {
  const source = filters ?? {};
  const out: Record<string, unknown[]> = {};
  for (const key of Object.keys(source).sort((x, y) => x.localeCompare(y))) {
    const values = source[key];
    if (values.length > 0) out[key] = values;
  }
  return out;
}

function numberOrNullEquals(a: number | null, b: number | null): boolean {
  return a === b;
}

function jsonEquals(a: unknown, b: unknown): boolean {
  // Stable stringify is overkill for our shapes; JSON.stringify order is
  // insertion-order deterministic for the shapes we use (objects built the
  // same way in serde, arrays positional). If this ever bites us, replace
  // with a structural walk.
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * File equality: same set of uploadIds with same listType, order-independent.
 * Other fields (name, rowCount, status) are metadata that can vary across
 * reads (server enriches name/rowCount; client's status is always 'done' after
 * hydration). Comparing uploadId+type is the stable business identity.
 */
interface FileLike {
  id?: string | null;
  listType?: string | null;
  listKind?: string | null;
}

function filesEqual(a: readonly FileLike[], b: readonly FileLike[]): boolean {
  if (a.length !== b.length) return false;
  const keyOf = (f: FileLike): string => `${f.id ?? ''}|${f.listType ?? ''}|${f.listKind ?? ''}`;
  const byKey = (x: string, y: string): number => x.localeCompare(y);
  const aKeys = a.map(keyOf).sort(byKey);
  const bKeys = b.map(keyOf).sort(byKey);
  return aKeys.every((k, i) => k === bKeys[i]);
}

interface ContentLinkLike {
  url?: string;
  name?: string;
  type?: string;
}

function contentLinksEqual(a: readonly ContentLinkLike[], b: readonly ContentLinkLike[]): boolean {
  if (a.length !== b.length) return false;
  // Order matters — insertion order is the persistence contract.
  return a.every((al, i) => {
    const bl = b[i];
    return (
      al.url === bl?.url &&
      (al.name ?? '') === (bl?.name ?? '') &&
      (al.type ?? '') === (bl?.type ?? '')
    );
  });
}

interface ContentFileLike {
  id?: string;
  type?: string;
  name?: string;
}

function contentFilesEqual(a: readonly ContentFileLike[], b: readonly ContentFileLike[]): boolean {
  if (a.length !== b.length) return false;
  // Order matches the persistence contract — insertion order is the source
  // of truth (same as contentLinksEqual). Reordering must dirty the patch.
  return a.every((af, i) => {
    const bf = b[i];
    return (
      (af.id ?? '') === (bf?.id ?? '') &&
      (af.type ?? '') === (bf?.type ?? '') &&
      (af.name ?? '') === (bf?.name ?? '')
    );
  });
}
